import { action, computed, observable } from 'mobx';
import { IShippingMethod } from '../../domain/entities/ShippingMethodsCollection/ShippingMethodsCollection';
import SetShippingMethodUseCase from '../../use-cases/SetShippingMethodUseCase';
import formatPrice from '../../utils/PriceFormatUtils';
import SHIPPING_METHODS from '../../constants/ShippingMethods';
import TransactionRepository from '../../repositories/TransactionRepository';
import Transaction from '../../domain/entities/Transaction/Transaction';
import ConfigRepository from '../../repositories/ConfigRepository';
import Config from '../../domain/entities/Config';
import AddressRepository from '../../repositories/AddressRepository';
import AddressFormStore from '../../domain/entities/AddressFormStore';
import { calculateShippingPriceWithTax } from '../../utils/ShippingMethodUtils';
import { FIELD_TYPE } from '../../constants/Constants';
import ShippingUIState from '../../stores/UIState/ShippingUIState';

export interface IShippingViewControllerConfig {
  transactionRepository: TransactionRepository;
  configRepository: ConfigRepository;
  shippingAddressRepository: AddressRepository;
  shippingUIState: ShippingUIState;
}

interface IShippingViewControllerData {
  selectedShippingMethodId: SHIPPING_METHODS;
  shippingMethods: IShippingMethod[];
  showStrikeoffPrice: boolean;
  isPOA: boolean;
  locale: string;
  isShippingMethodsMissing: boolean;
}

export default class ShippingViewController {
  private transactionRepository: TransactionRepository;

  private configRepository: ConfigRepository;

  private shippingAddressRepository: AddressRepository;

  @observable private transaction: Transaction;

  @observable private shippingUIState: ShippingUIState;

  constructor(config: IShippingViewControllerConfig) {
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
    this.shippingAddressRepository = config.shippingAddressRepository;
    this.shippingUIState = config.shippingUIState;
  }

  async loaded(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    this.setTransaction(transaction);
  }

  @action
  setTransaction = (transaction: Transaction): void => {
    this.transaction = transaction;
  };

  @computed
  get data(): IShippingViewControllerData {
    return {
      isPOA: this.transactionRepository.transaction?.isPOA || false,
      isShippingMethodsMissing: this.shippingUIState.isShippingMethodsMissing,
      locale: this.config?.locale,
      selectedShippingMethodId: this.transaction
        ? this.transaction.selectedShippingMethodId
        : SHIPPING_METHODS.NONE,
      shippingMethods: this.shippingMethods,
      showStrikeoffPrice: this.config?.showStrikeoffPrice,
    };
  }

  @computed
  get selectedCountryId(): number {
    // Selected country id should be one of the following
    // 1. if user add new address or edit saved address the selected country id is used from form store
    // 2. for return user with selected saved address from address list, the selected country id is used from transaction
    // 3. if can not find the country id in above cases then selected country id fallbacks to the site config country id
    if (this.transactionRepository.isNewOrEditAddress) {
      return this.formStore.values[FIELD_TYPE.COUNTRY_ID];
    }
    return this.transaction?.shippingAddress?.attributes?.country_id || this.config.countryId;
  }

  @computed
  get shippingMethods(): IShippingMethod[] {
    const { displayShippingFeeWithTax, vatPercentage } = this.config;
    const methods = (this.transaction?.shippingMethods?.items || [])
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return calculateShippingPriceWithTax(methods, {
      displayShippingFeeWithTax,
      vatPercentage,
    });
  }

  private get config(): Config {
    const { config } = this.configRepository;
    return config;
  }

  private get formStore(): AddressFormStore {
    return this.shippingAddressRepository.formStore;
  }

  public getPrice = (price: number, usefreeLabelForZero?: string): string => {
    if (price === 0 ) return usefreeLabelForZero;
    return price === undefined ? '' : this.config && formatPrice(this.config, price);
  };

  public setShippingMethod = async (shippingMethodId: SHIPPING_METHODS): Promise<void> => {
    if (shippingMethodId === this.transaction?.selectedShippingMethodId) return;
    const useCase = new SetShippingMethodUseCase(shippingMethodId);
    await useCase.execute();
    // For multiple country shipping sites, after new ship method selected
    // the latest transaction data may contain all country's ship methods
    // and need to get the selected country's ship methods again
    this.transactionRepository.loadShippingMethodsForCrossBorderSite(this.selectedCountryId);
  };
}
